/*
[GNU Affero General Public License (AGPL)](https://www.gnu.org/licenses/agpl-3.0.html)

VeloceSupabase - A library for working with supabase made in Angular v17+ made with care by Colibryx Srl in Italy.
Copyright (C) 2025 Colibryx S.r.l.

This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as published by
the Free Software Foundation, either version 3 of the License, or
(at your option) any later version.

This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
GNU Affero General Public License for more details.

You should have received a copy of the GNU Affero General Public License
along with this program. If not, see <https://www.gnu.org/licenses/>.
*/

import { type SupabaseClient } from "@supabase/supabase-js";
import { Observable, BehaviorSubject } from "rxjs";
import { distinctUntilChanged, filter, take, map } from "rxjs/operators";
import type { Database } from "../../../../@schema/db";
import { LoggerService } from "@app/@services/shared/logger.service";
import { inject } from "@angular/core";

/**
 * Centralized manager for Supabase Realtime connection state
 * Handles connection monitoring, state tracking, and readiness verification
 */
export class SupabaseRealtimeManager {
    private readonly logger = inject(LoggerService);
    // Static properties for centralized Realtime connection state management
    private static realtimeConnectionState$ = new BehaviorSubject<string>('closed');
    private static realtimeStateCallbacksSetup = false;
    private static realtimeStateMonitor: any = null;
    private static instance: SupabaseRealtimeManager | null = null;

    private constructor(private supabaseClient: SupabaseClient<Database>) { }

    /**
     * Get or create singleton instance
     */
    public static getInstance(supabaseClient: SupabaseClient<Database>): SupabaseRealtimeManager {
        if (!SupabaseRealtimeManager.instance) {
            SupabaseRealtimeManager.instance = new SupabaseRealtimeManager(supabaseClient);
            SupabaseRealtimeManager.instance.setupRealtimeStateMonitoring();
        }
        return SupabaseRealtimeManager.instance;
    }

    /**
     * Setup centralized realtime state monitoring with improved timing
     */
    private setupRealtimeStateMonitoring(): void {
        if (SupabaseRealtimeManager.realtimeStateCallbacksSetup) {
            return;
        }

        this.logger.logInfo("[SUPABASE REALTIME] Setting up connection state monitoring");

        const client = this.supabaseClient.realtime;

        // Function to check and update current state
        const updateCurrentState = () => {
            const currentState = client.connectionState().toLowerCase();
            const previousState = SupabaseRealtimeManager.realtimeConnectionState$.value;

            if (currentState !== previousState) {
                this.logger.logInfo(`[SUPABASE REALTIME] State changed: ${previousState} -> ${currentState}`);
                SupabaseRealtimeManager.realtimeConnectionState$.next(currentState);
            }
        };

        // Add state change callbacks
        const openCallback = () => {
            this.logger.logInfo("[SUPABASE REALTIME] CONNECTION OPENED (callback)");
            SupabaseRealtimeManager.realtimeConnectionState$.next('open');
        };

        const closeCallback = () => {
            this.logger.logInfo("[SUPABASE REALTIME] CONNECTION CLOSED (callback)");
            SupabaseRealtimeManager.realtimeConnectionState$.next('closed');
        };

        const errorCallback = (error: any) => {
            this.logger.logInfo("[SUPABASE REALTIME] CONNECTION ERROR (callback)", error);
            SupabaseRealtimeManager.realtimeConnectionState$.next('error');
        };

        // Add callbacks
        client.stateChangeCallbacks.open.push(openCallback);
        client.stateChangeCallbacks.close.push(closeCallback);
        client.stateChangeCallbacks.error.push(errorCallback);

        // Set initial state and start monitoring
        updateCurrentState();

        // Start a timer to periodically check connection state
        // This handles cases where callbacks might not fire properly
        SupabaseRealtimeManager.realtimeStateMonitor = setInterval(() => {
            updateCurrentState();
        }, 1000);

        SupabaseRealtimeManager.realtimeStateCallbacksSetup = true;
        this.logger.logInfo("[SUPABASE REALTIME] Monitoring setup complete");
    }

    /**
     * Returns an Observable that emits when Realtime is connected
     * If already connected, emits immediately. Otherwise waits for connection.
     */
    public onRealtimeConnected(): Observable<void> {
        this.logger.logInfo("[SUPABASE REALTIME] onRealtimeConnected called");

        // Check if already connected
        if (this.isRealtimeConnected()) {
            this.logger.logInfo("[SUPABASE REALTIME] Already connected, emitting immediately");
            return new Observable(subscriber => {
                subscriber.next();
                subscriber.complete();
            });
        }

        this.logger.logInfo("[SUPABASE REALTIME] Not connected, waiting for connection...");

        return SupabaseRealtimeManager.realtimeConnectionState$.pipe(
            filter(state => {
                this.logger.logInfo("[SUPABASE REALTIME] State in filter:", state);
                return state === 'open';
            }),
            take(1),
            map(() => {
                this.logger.logInfo("[SUPABASE REALTIME] Connection established, emitting");
                return undefined;
            })
        );
    }

    /**
     * Returns an Observable that emits the current connection state and all future changes
     */
    public onRealtimeStateChange(): Observable<string> {
        return SupabaseRealtimeManager.realtimeConnectionState$.pipe(
            distinctUntilChanged()
        );
    }

    /**
     * Check if Realtime is currently connected
     */
    public isRealtimeConnected(): boolean {
        const isConnected = this.supabaseClient.realtime.isConnected();
        const currentState = this.supabaseClient.realtime.connectionState().toLowerCase();
        this.logger.logInfo(`[SUPABASE REALTIME] isRealtimeConnected: ${isConnected}, currentState: ${currentState}`);
        return isConnected;
    }

    /**
     * Get current Realtime connection state
     */
    public getRealtimeConnectionState(): string {
        return this.supabaseClient.realtime.connectionState().toLowerCase();
    }

    /**
     * Wait for Realtime connection with Promise (useful for async/await)
     */
    public waitForRealtimeConnection(timeoutMs: number = 15000): Promise<boolean> {
        this.logger.logInfo(`[SUPABASE REALTIME] waitForRealtimeConnection called with timeout: ${timeoutMs}ms`);

        return new Promise((resolve) => {
            if (this.isRealtimeConnected()) {
                this.logger.logInfo("[SUPABASE REALTIME] Already connected in promise");
                resolve(true);
                return;
            }

            const startTime = Date.now();
            const subscription = this.onRealtimeStateChange().subscribe((state) => {
                this.logger.logInfo(`[SUPABASE REALTIME] State change in promise: ${state}`);

                if (state === 'open') {
                    subscription.unsubscribe();
                    this.logger.logInfo("[SUPABASE REALTIME] Connection established in promise");
                    resolve(true);
                } else if (Date.now() - startTime > timeoutMs) {
                    subscription.unsubscribe();
                    this.logger.logInfo("[SUPABASE REALTIME] Timeout in promise");
                    resolve(false);
                }
            });
        });
    }

    /**
     * Test if Realtime is ready to accept channel subscriptions
     * This creates a test channel to verify the system is fully initialized
     */
    public testRealtimeChannelCapability(): Promise<boolean> {
        return new Promise((resolve) => {
            if (!this.isRealtimeConnected()) {
                this.logger.logInfo("[SUPABASE REALTIME] Not connected, channel test failed");
                resolve(false);
                return;
            }

            const testChannelName = `realtime_test_${Date.now()}`;
            this.logger.logInfo(`[SUPABASE REALTIME] Testing channel capability with: ${testChannelName}`);

            const testChannel = this.supabaseClient.channel(testChannelName);

            let resolved = false;
            const timeout = setTimeout(() => {
                if (!resolved) {
                    resolved = true;
                    this.logger.logInfo("[SUPABASE REALTIME] Channel test timeout");
                    testChannel.unsubscribe();
                    resolve(false);
                }
            }, 3000);

            testChannel.subscribe((status) => {
                if (!resolved) {
                    resolved = true;
                    clearTimeout(timeout);

                    if (status === 'SUBSCRIBED') {
                        this.logger.logInfo("[SUPABASE REALTIME] Channel test successful");
                        testChannel.unsubscribe();
                        resolve(true);
                    } else {
                        this.logger.logInfo(`[SUPABASE REALTIME] Channel test failed with status: ${status}`);
                        testChannel.unsubscribe();
                        resolve(false);
                    }
                }
            });
        });
    }

    /**
     * Enhanced method that waits for connection AND verifies channel capability
     */
    public onRealtimeFullyReady(): Observable<void> {
        this.logger.logInfo("[SUPABASE REALTIME] onRealtimeFullyReady called");

        return new Observable(subscriber => {
            // First wait for connection
            this.onRealtimeConnected().subscribe({
                next: async () => {
                    this.logger.logInfo("[SUPABASE REALTIME] Connection ready, testing channel capability...");

                    // Then test channel capability
                    const isReady = await this.testRealtimeChannelCapability();

                    if (isReady) {
                        this.logger.logInfo("[SUPABASE REALTIME] Fully ready!");
                        subscriber.next();
                        subscriber.complete();
                    } else {
                        this.logger.logInfo("[SUPABASE REALTIME] Channel test failed, retrying...");
                        // Retry after a short delay
                        setTimeout(async () => {
                            const retryReady = await this.testRealtimeChannelCapability();
                            if (retryReady) {
                                this.logger.logInfo("[SUPABASE REALTIME] Fully ready after retry!");
                                subscriber.next();
                                subscriber.complete();
                            } else {
                                console.warn("[SUPABASE REALTIME] Still not ready after retry, proceeding anyway");
                                subscriber.next();
                                subscriber.complete();
                            }
                        }, 500);
                    }
                },
                error: (error) => {
                    console.error("[SUPABASE REALTIME] Connection error in onRealtimeFullyReady", error);
                    subscriber.error(error);
                }
            });
        });
    }

    /**
     * Alias for onRealtimeFullyReady() - more convenient method name
     * This is the recommended method to use when you need to start Realtime operations
     */
    public onRealtimeReady(): Observable<void> {
        return this.onRealtimeFullyReady();
    }

    /**
     * Cleanup static resources
     */
    public static cleanup(): void {
        if (SupabaseRealtimeManager.realtimeStateMonitor) {
            clearInterval(SupabaseRealtimeManager.realtimeStateMonitor);
            SupabaseRealtimeManager.realtimeStateMonitor = null;
        }
        SupabaseRealtimeManager.realtimeStateCallbacksSetup = false;
        SupabaseRealtimeManager.instance = null;
    }
} 